import { Box, Stack } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { Common, Custom, Page, Workflow } from "components";
import { Utils, useFlow } from "components/workflow";
import {
  createDefaultFeedback,
  createDefaultSettings,
  createDefaultUser,
  ICategory,
  IConfiguration,
  IMenu,
  IPotluck,
  IUser,
} from "interfaces";
import { useApp, useData, useService, useSession, useTheme } from "contexts";
import { ChevronLeftRounded } from "@mui/icons-material";
import { usePotluck } from "hooks";
import { Enums, Routes } from "utils";
import { Auxiliars } from "helpers";
import { MessageType, SessionType } from "types";

const Contributor = () => {
  const { theme } = useTheme();
  const { login } = useSession();
  const { setFeedback, t } = useApp();
  const { emailService, potluckService, userService } = useService();

  const onSubmit = async (
    name: string,
    data: Workflow.Utils.ContributorType
  ) => {
    const { user, potluck } = data;

    if (user.role === "guest") {
      const userResponse = await userService.insert({
        ...createDefaultUser(),
        _id: user._id,
        email: user.email,
        first_name: user.first_name,
      });

      if (!userResponse) {
        return setFeedback({
          message: t.message.feedback.request_error,
          severity: Enums.EnumFeedback.Error,
        } as MessageType);
      }

      login({
        id: userResponse._id,
        name: userResponse.full_name,
        email: userResponse.email,
        role: userResponse.role,
        settings: userResponse.settings,
      } as SessionType);
    }
    // else if (user.role === "user") {
    //   login({
    //     id: user._id,
    //     name: user.full_name,
    //     email: user.email,
    //     role: user.role,
    //     settings: createDefaultSettings(),
    //   } as SessionType);
    // }

    if (name === Enums.EnumFlow.Contributor) {
      // TODO : move somewhere else
      potluck.menu = potluck.menu
        .map((menu) => ({
          ...menu,
          categories: menu.categories.filter((cat) => cat.name.length > 0),
        }))
        .filter((menu) => menu.categories.length > 0);

      const updatedPotluck = Auxiliars.walkAndReplace<IPotluck>(potluck, [
        {
          path: "menu.configuration",
          value: (conf: IConfiguration) => conf._id,
        },
        {
          path: "menu.categories",
          value: (cats: ICategory[]) =>
            cats.filter((cat) => cat.name.length > 0),
        },
        {
          path: "menu.categories.suggested_by",
          value: (user: IUser) => user._id,
        },
        {
          path: "menu.categories.suggested_by",
          override: false,
          value: user._id,
        },
        {
          path: "menu.categories.assigned_to",
          value: user._id,
        },
        {
          path: "participants",
          value: (users: IUser[]) => users.map((user) => user._id),
        },
        {
          path: "participants",
          value: (users: string[]) =>
            users.includes(user._id) ? users : users.concat(user._id),
        },
      ]);

      const response = await potluckService.update(updatedPotluck);

      if (response) {
        // TODO : send email
        emailService.contributor(
          user.email,
          t.message.subject.contributor,
          user._id,
          response._id,
          user.full_name,
          user.role
        );

        return setFeedback({
          message: t.message.feedback.request_create_success,
          severity: Enums.EnumFeedback.Success,
        } as MessageType);
      } else {
        return setFeedback({
          message: t.message.feedback.request_error,
          severity: Enums.EnumFeedback.Error,
        } as MessageType);
      }
    }
  };

  const {
    current,
    data,
    direction,
    getVariants,
    handlePrev,
    handleSubmit,
    step,
    StepComponent,
    stepRef,
    steps,
    updateData,
  } = useFlow<Workflow.Utils.ContributorType>(
    {
      name: Enums.EnumFlow.Contributor,
      count: Utils.ContributorSteps.length,
      step: 1,
      route: Routes.userdashboard,
    },
    onSubmit
  );

  if (!current) return <Page.Loader />;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        border: 1,
        overflow: "hidden",
        mt: theme.spacing.md,
        px: theme.spacing.lg,
        pt: theme.spacing.lg,
        pb: theme.spacing.xxl,
        borderRadius: theme.border.radius,
        borderColor: theme.color.primary.accent,
      }}
    >
      <AnimatePresence mode="wait">
        <Box
          component={motion.div}
          key={step}
          variants={getVariants(direction)}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.4 }}
        >
          <StepComponent.Component
            ref={stepRef}
            data={data}
            updateData={updateData}
          />
        </Box>
      </AnimatePresence>

      <Box mt={theme.spacing.sm}>
        <Common.Divider sx={{ backgroundColor: theme.color.border.accent }} />
        <Stack direction="row" spacing={theme.spacing.xs}>
          {step !== steps && (
            <>
              <Custom.Button onClick={handlePrev} secondary>
                <ChevronLeftRounded />
              </Custom.Button>
              <Custom.Button
                submit
                fullWidth
                trailing="right"
                variant="contained"
              >
                {t.action.next}
              </Custom.Button>
            </>
          )}
          {step === steps && (
            <Custom.Button
              submit
              fullWidth
              trailing="right"
              variant="contained"
            >
              {t.action.finish}
            </Custom.Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export const Wrapper = () => {
  const { user } = useData();
  const { potluck } = usePotluck();

  if (!potluck) return <Page.Loader />;

  return (
    <Workflow.Wrapper
      initData={{
        potluck: potluck,
        user: user || createDefaultUser(),
        feedback: createDefaultFeedback(),
      }}
    >
      <Contributor />
    </Workflow.Wrapper>
  );
};
